import type { EntityState } from '@reduxjs/toolkit';

// TODO: it is better to make all these methods more generic & modularized

type References<
  K extends string = 'topics' | 'flowSteps' | 'behaviour' | 'stopKeywords',
> = {
  [P in K]: Record<string, boolean>;
};

// Flows & Steps

export function handleCreateFlowReferences<K extends string>(
  steps: Array<{
    id: string;
    references: Record<string, Record<string, boolean>>;
  }>,
  referenceKey: string,
  flowId: string,
  addReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'flowSteps',
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  steps.forEach(step => {
    const refObj = step.references[referenceKey];
    Object.keys(refObj || {}).forEach(ref => {
      addReference(entityState, ref, 'flowSteps', `${flowId}/${step.id}`);
    });
  });
}

export function handleImportFlowReferences<K extends string>(
  steps: Array<{
    id: string;
    references: Record<string, Record<string, boolean>>;
  }>,
  referenceKey: string,
  flowId: string,
  addReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'flowSteps',
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  steps.forEach(step => {
    Object.keys(step.references[referenceKey] || {}).forEach(ref => {
      addReference(entityState, ref, 'flowSteps', `${flowId}/${step.id}`);
    });
  });
}

export function handleDeleteFlowReferences<
  T extends { id: string; references?: References<K> },
  K extends string,
>(
  entities: T[],
  flowId: string,
  removeReferencePrefix: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'flowSteps',
    prefix: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  entities.forEach(entity => {
    removeReferencePrefix(entityState, entity.id, 'flowSteps', flowId);
  });
}

export function handleDeleteNoCodeStepReferences<
  T extends { id: string; references?: References<K> },
  K extends string,
>(
  entities: T[],
  flowId: string,
  removeReferencePrefix: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'noCodeSteps',
    prefix: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  entities.forEach(entity => {
    removeReferencePrefix(entityState, entity.id, 'noCodeSteps', flowId);
  });
}

export function handleDeleteFlowStepReferences<
  T extends { id: string; references?: References<'flowSteps'> },
  K extends string,
>(
  entities: T[],
  flowId: string,
  stepId: string,
  removeReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'flowSteps',
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  const stepReferenceKey = `${flowId}/${stepId}`;

  entities
    .filter(
      entity => entity.references?.flowSteps?.[stepReferenceKey] !== undefined,
    )
    .forEach(entity => {
      removeReference(entityState, entity.id, 'flowSteps', stepReferenceKey);
    });
}

export function handleDeleteFlowNoCodeStepReferences<
  T extends { id: string; references?: References<'noCodeSteps'> },
  K extends string,
>(
  entities: T[],
  flowId: string,
  stepId: string,
  removeReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'noCodeSteps',
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  const stepReferenceKey = `${flowId}/${stepId}`;

  entities
    .filter(
      entity =>
        entity.references?.noCodeSteps?.[stepReferenceKey] !== undefined,
    )
    .forEach(entity => {
      removeReference(entityState, entity.id, 'noCodeSteps', stepReferenceKey);
    });
}

export function handleNoCodeStepReferenceUpdates<
  T extends { id: string; references?: References<'noCodeSteps'> },
  K extends string,
>(
  entities: Record<string, T | undefined>,
  stepReferences: Record<string, boolean>,
  flowId: string,
  stepId: string,
  addReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'noCodeSteps',
    id: string,
  ) => void,
  removeReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'noCodeSteps',
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  const stepReferenceKey = `${flowId}/${stepId}`;

  // Add references for entities that reference this step
  const entitiesToAdd = Object.keys(stepReferences)
    .map(ref => entities[ref])
    .filter((entity): entity is T => entity !== undefined);

  for (const entity of entitiesToAdd) {
    addReference(entityState, entity.id, 'noCodeSteps', stepReferenceKey);
  }

  // Remove references for entities that no longer reference this step
  Object.values(entities)
    .filter(
      (entity): entity is T =>
        entity !== undefined &&
        entity.references?.noCodeSteps?.[stepReferenceKey] !== undefined &&
        stepReferences[entity.id] === undefined,
    )
    .forEach(entity => {
      removeReference(entityState, entity.id, 'noCodeSteps', stepReferenceKey);
    });
}

export function handleStepReferenceUpdates<
  T extends { id: string; references?: References<'flowSteps'> },
  K extends string,
>(
  entities: Record<string, T | undefined>,
  stepReferences: Record<string, boolean>,
  flowId: string,
  stepId: string,
  addReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'flowSteps',
    id: string,
  ) => void,
  removeReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'flowSteps',
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  const stepReferenceKey = `${flowId}/${stepId}`;

  // Add references for entities that reference this step
  const entitiesToAdd = Object.keys(stepReferences)
    .map(ref => entities[ref])
    .filter((entity): entity is T => entity !== undefined);

  for (const entity of entitiesToAdd) {
    addReference(entityState, entity.id, 'flowSteps', stepReferenceKey);
  }

  // Remove references for entities that no longer reference this step
  Object.values(entities)
    .filter(
      (entity): entity is T =>
        entity !== undefined &&
        entity.references?.flowSteps?.[stepReferenceKey] !== undefined &&
        stepReferences[entity.id] === undefined,
    )
    .forEach(entity => {
      removeReference(entityState, entity.id, 'flowSteps', stepReferenceKey);
    });
}

// Topics
export function handleCreateTopicReferences<
  T extends { id: string; references?: References<'topics'> },
  K extends string,
>(
  entities: Record<string, T | undefined>,
  references: Record<string, boolean>,
  topicId: string,
  addReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'topics',
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  const entitiesToAdd = Object.keys(references)
    .map(ref => entities[ref])
    .filter((entity): entity is T => entity !== undefined);

  for (const entity of entitiesToAdd) {
    addReference(entityState, entity.id, 'topics', topicId);
  }
}
export function handleTopicReferenceUpdates<
  T extends { id: string; references?: References<'topics'> },
  K extends string,
>(
  entities: Record<string, T | undefined>,
  references: Record<string, boolean>,
  topicId: string,
  addReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'topics',
    id: string,
  ) => void,
  removeReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'topics',
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  // Add references for entities that reference this topic
  const entitiesToAdd = Object.keys(references)
    .map(ref => entities[ref])
    .filter((entity): entity is T => entity !== undefined);

  for (const entity of entitiesToAdd) {
    addReference(entityState, entity.id, 'topics', topicId);
  }

  // Remove references for entities that no longer reference this topic
  Object.values(entities)
    .filter(
      (entity): entity is T =>
        entity !== undefined &&
        entity.references?.topics?.[topicId] !== undefined &&
        references[entity.id] === undefined,
    )
    .forEach(entity => {
      removeReference(entityState, entity.id, 'topics', topicId);
    });
}

export function handleDeleteTopicReferences<
  T extends { id: string; references?: References<'topics'> },
  K extends string,
>(
  entities: Record<string, T | undefined>,
  topicId: string,
  removeReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'topics',
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  Object.values(entities)
    .filter(entity => entity?.references?.topics?.[topicId] !== undefined)
    .forEach(entity => {
      entity && removeReference(entityState, entity.id, 'topics', topicId);
    });
}

// Rules

export const BEHAVIOUR_REFERENCE_KEY = 'GLOBAL_BEHAVIOUR';
export const GREETING_REFERENCE_KEY = 'GREETING';
export const VOICE_GREETING_REFERENCE_KEY = 'VOICE_GREETING';
export const WEB_CHAT_GREETING_REFERENCE_KEY = 'WEB_CHAT_GREETING';
export const DISCLAIMER_REFERENCE_KEY = 'DISCLAIMER_MESSAGE';
export const VOICE_DISCLAIMER_REFERENCE_KEY = 'VOICE_DISCLAIMER_MESSAGE';
export const ROLE_REFERENCE_KEY = 'ROLE';
export const PERSONALITY_REFERENCE_KEY = 'PERSONALITY';

export function updateRulesReferences<
  K extends string,
  T extends { id: string; references?: References<'behaviour'> },
>(
  entities: Record<string, T | undefined>,
  behaviourReferences: Record<string, boolean>,
  addReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'behaviour',
    id: string,
  ) => void,
  removeReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'behaviour',
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  const entitiesToAdd = Object.keys(behaviourReferences)
    .map(ref => entities[ref])
    .filter((entity): entity is T => entity !== undefined);

  for (const entity of entitiesToAdd) {
    addReference(entityState, entity.id, 'behaviour', BEHAVIOUR_REFERENCE_KEY);
  }

  // Remove references for entities that no longer reference this step
  Object.values(entities)
    .filter(
      (entity): entity is T =>
        entity !== undefined &&
        entity.references?.behaviour?.[BEHAVIOUR_REFERENCE_KEY] !== undefined &&
        behaviourReferences[entity.id] === undefined,
    )
    .forEach(entity => {
      removeReference(
        entityState,
        entity.id,
        'behaviour',
        BEHAVIOUR_REFERENCE_KEY,
      );
    });
}

// Generic helper for singleton-like entities (rules, greeting, role, personality)
export function updateSingletonReferences<
  K extends string,
  T extends { id: string; references?: References<K> },
  RefKey extends K,
>(
  entities: Record<string, T | undefined>,
  singletonId: string,
  singletonReferences: Record<string, boolean>,
  addReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: RefKey,
    id: string,
  ) => void,
  removeReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: RefKey,
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
  refKey: RefKey,
): void {
  const entitiesToAdd = Object.keys(singletonReferences)
    .map(ref => entities[ref])
    .filter((entity): entity is T => entity !== undefined);

  for (const entity of entitiesToAdd) {
    addReference(entityState, entity.id, refKey, singletonId);
  }

  Object.values(entities)
    .filter(
      (entity): entity is T =>
        entity !== undefined &&
        entity.references?.[refKey]?.[singletonId] !== undefined &&
        singletonReferences[entity.id] === undefined,
    )
    .forEach(entity => {
      removeReference(entityState, entity.id, refKey, singletonId);
    });
}

// Stop keywords
export function updateStopKeywordsReferences<
  K extends string,
  T extends { id: string; references?: References<'stopKeywords'> },
>(
  entities: Record<string, T | undefined>,
  stopKeywordId: string,
  stopKeywordsReferences: Record<string, boolean>,
  addReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'stopKeywords',
    id: string,
  ) => void,
  removeReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'stopKeywords',
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  const entitiesToAdd = Object.keys(stopKeywordsReferences)
    .map(ref => entities[ref])
    .filter((entity): entity is T => entity !== undefined);

  for (const entity of entitiesToAdd) {
    addReference(entityState, entity.id, 'stopKeywords', stopKeywordId);
  }

  // Remove references for entities that no longer reference this stop keyword
  Object.values(entities)
    .filter(
      (entity): entity is T =>
        entity !== undefined &&
        entity.references?.stopKeywords?.[stopKeywordId] !== undefined &&
        stopKeywordsReferences[entity.id] === undefined,
    )
    .forEach(entity => {
      removeReference(entityState, entity.id, 'stopKeywords', stopKeywordId);
    });
}

export function deleteStopKeywordsReferences<
  T extends { id: string; references?: References<'stopKeywords'> },
  K extends string,
>(
  entities: Record<string, T | undefined>,
  stopKeywordId: string,
  removeReference: (
    entityState: EntityState<{ references?: References<K> }, string>,
    parentId: string,
    ref: 'stopKeywords',
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<K> }, string>,
): void {
  Object.values(entities)
    .filter(
      entity => entity?.references?.stopKeywords?.[stopKeywordId] !== undefined,
    )
    .forEach(entity => {
      entity &&
        removeReference(entityState, entity.id, 'stopKeywords', stopKeywordId);
    });
}

// Generic
export function handleUpdateEntityReferences<
  EntityRefs extends string,
  T extends { id: string; references?: References<EntityRefs> },
  K extends EntityRefs,
>(
  entities: Record<string, T | undefined>,
  entityId: string,
  entityReferences: Record<string, boolean>,
  addReference: (
    entityState: EntityState<{ references?: References<EntityRefs> }, string>,
    parentId: string,
    ref: EntityRefs,
    id: string,
  ) => void,
  removeReference: (
    entityState: EntityState<{ references?: References<EntityRefs> }, string>,
    parentId: string,
    ref: EntityRefs,
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<EntityRefs> }, string>,
  refKey: K,
): void {
  const entitiesToAdd = Object.keys(entityReferences)
    .map(ref => entities[ref])
    .filter((entity): entity is T => entity !== undefined);

  for (const entity of entitiesToAdd) {
    addReference(entityState, entity.id, refKey, entityId);
  }

  Object.values(entities)
    .filter(
      (entity): entity is T =>
        entity !== undefined &&
        entity.references?.[refKey]?.[entityId] !== undefined &&
        entityReferences[entity.id] === undefined,
    )
    .forEach(entity => {
      removeReference(entityState, entity.id, refKey, entityId);
    });
}

export function handleDeleteEntityReferences<
  EntityRefs extends string,
  T extends { id: string; references?: References<EntityRefs> },
  K extends EntityRefs,
>(
  entities: Record<string, T | undefined>,
  entityId: string,
  removeReference: (
    entityState: EntityState<{ references?: References<EntityRefs> }, string>,
    parentId: string,
    ref: EntityRefs,
    id: string,
  ) => void,
  entityState: EntityState<{ references?: References<EntityRefs> }, string>,
  refKey: K,
): void {
  Object.values(entities)
    .filter(entity => entity?.references?.[refKey]?.[entityId] !== undefined)
    .forEach(entity => {
      entity && removeReference(entityState, entity.id, refKey, entityId);
    });
}
